// ============================================================================
// SendPlanModal.tsx — programar el envío de una campaña.
//
// 24-jul-2026: la VELOCIDAD ya no se elige. Los presets (Rápido/Equilibrado/
// Seguro) se calculaban sobre el tamaño de la audiencia — cuanta más gente, más
// rápido, justo al revés de lo que conviene — y con ≤35 destinatarios el modal
// mandaba todo en un día sin preguntar. Ahora el ritmo lo fija el motor según la
// antigüedad del número (GET /campaigns/pace) y aquí solo se MUESTRA.
// Lo que sigue eligiéndose: horario y días de la semana. El disclaimer de riesgo
// es obligatorio en toda campaña.
// ============================================================================

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, CalendarClock, Loader2, Gauge, AlertTriangle } from 'lucide-react';
import type { Campaign } from '../types/campaign';
// Re-cableado a lealify-base (Inc10): programa contra /campaigns (config + schedule).
import { campaignsApi } from '../../../api/campaignsApi';
import type { Fatiga } from '../../../api/campaignsApi';
// Textos del ritmo: módulo COMPARTIDO con la agenda (/root/sites/_shared/lealify-campaigns).
import { textoRitmo, textoConsumo } from '../campaignRitmo';

// Resultado de programar (forma local; base devuelve {queued, est_days} y lo enriquecemos).
export interface ScheduleResult {
  totalQueued: number;
  sendPlan: { msgsPerDay: number };
  avgIntervalMinutes: number;
  startDay: string;
  endDay: string;
  msgsPerDayAdjusted: number | null;
  skipped: { noPhone: number; duplicates: number };
}

const WINDOW_PRESETS = [
  { key: 'morning', label: '🌅 Mañana', start: '09:00', end: '13:00' },
  { key: 'afternoon', label: '🌇 Tarde', start: '13:00', end: '19:00' },
  { key: 'business', label: '🏪 Comercial', start: '10:00', end: '18:00' },
  { key: 'custom', label: '⚙️ Personalizado', start: '', end: '' },
] as const;

const WEEKDAY_LABELS = ['D', 'L', 'M', 'M', 'J', 'V', 'S']; // índice = getDay()

interface SendPlanModalProps {
  campaign: Campaign;
  onClose: () => void;
  onScheduled: (result: ScheduleResult) => void;
}

function minutesOf(hhmm: string): number | null {
  const m = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(hhmm);
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
}

const SendPlanModal: React.FC<SendPlanModalProps> = ({ campaign, onClose, onScheduled }) => {
  const [pendingTotal, setPendingTotal] = useState<number | null>(null);
  const [fatiga, setFatiga] = useState<Fatiga | null>(null);
  const [loadingTotal, setLoadingTotal] = useState(true);

  // Ritmo que impone el motor (no editable): {day_cap, hour_cap, reason...}
  // `usado_hoy` es del NÚMERO, no de esta campaña: incluye otras campañas, las
  // felicitaciones de cumpleaños y los seguimientos. Por eso hay que enseñarlo — si no,
  // el operador ve "10 al día" y no entiende por qué solo le salieron 4. (24-jul-2026)
  // Campaña MANUAL: el horario, los días y la estimación no gobiernan nada — envía una
  // persona desde la cola. Aquí solo se prepara la lista.
  const esAsistida = campaign.dispatch === 'assisted';
  const [pace, setPace] = useState<{
    day_cap: number; hour_cap: number; days: number; repaired: boolean; reason: string;
    usado_hoy?: number; disponible?: number; reserva_cumples?: number;
    desglose_hoy?: { bulk: number; tx: number; total: number; por_fuente: { fuente: string; clase: string; enviados: number }[] };
  } | null>(null);
  const [windowKey, setWindowKey] = useState<typeof WINDOW_PRESETS[number]['key']>('business');
  const [customStart, setCustomStart] = useState('10:00');
  const [customEnd, setCustomEnd] = useState('18:00');
  const [weekdays, setWeekdays] = useState<number[]>([1, 2, 3, 4, 5]);
  const [disclaimer, setDisclaimer] = useState(false);
  // Tope propio de ESTA campaña. `null` = usar el techo del número.
  // El cambio del 24-jul quitó los presets de velocidad y, de paso, dejó al negocio sin
  // forma de pedir MENOS: el modal escribía msgs_per_day = day_cap, o sea el máximo. El
  // motor siempre respetó el número de la campaña (campaignWorker: min(msgs_per_day,
  // presupuesto)); lo que faltaba era dónde escribirlo. Subir sigue siendo imposible.
  const [topePropio, setTopePropio] = useState<number | null>(null);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ScheduleResult | null>(null);

  // ── Total de la audiencia (para estimar cuánto tardará) — preview contra base ──
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const n = await campaignsApi.previewCampaign(campaign.id);
        if (!cancelled) setPendingTotal(n);
      } catch {
        if (!cancelled) setPendingTotal(null);
      } finally {
        if (!cancelled) setLoadingTotal(false);
      }
    })();
    return () => { cancelled = true; };
  }, [campaign.id]);

  // ── Fatiga: quién de esta lista ya recibió OTRA campaña hace poco (3-sep-2026) ──
  // Solo se AVISA; programar sigue siendo decisión del negocio. El servidor deja rastro del
  // aviso al programar, así que aquí no hay nada que registrar.
  useEffect(() => {
    let cancelled = false;
    campaignsApi.fatiga(campaign.id)
      .then(f => { if (!cancelled) setFatiga(f); })
      .catch(() => { if (!cancelled) setFatiga(null); });
    return () => { cancelled = true; };
  }, [campaign.id]);

  // ── Ritmo del negocio (lo decide el motor por la antigüedad del número) ──
  useEffect(() => {
    let cancelled = false;
    campaignsApi.pace()
      .then(p => { if (!cancelled) setPace(p); })
      .catch(() => { if (!cancelled) setPace(null); });
    return () => { cancelled = true; };
  }, []);

  // ── Ventana efectiva ──
  const windowRange = useMemo(() => {
    const preset = WINDOW_PRESETS.find(w => w.key === windowKey)!;
    const start = windowKey === 'custom' ? customStart : preset.start;
    const end = windowKey === 'custom' ? customEnd : preset.end;
    const startMin = minutesOf(start);
    const endMin = minutesOf(end);
    const valid = startMin !== null && endMin !== null && endMin - startMin >= 120;
    return { start, end, minutes: valid ? (endMin! - startMin!) : 0, valid };
  }, [windowKey, customStart, customEnd]);

  const N = pendingTotal ?? 0;
  // El TECHO lo pone el motor. Mientras carga se asume el escalón más bajo (5) para
  // no anunciar una velocidad mayor que la real. El negocio puede pedir menos, nunca más.
  const techoNumero = pace?.day_cap ?? 5;
  const effectivePerDay = Math.max(1, Math.min(topePropio ?? techoNumero, techoNumero));

  // Días estimados contando solo días de la semana elegidos
  const estimate = useMemo(() => {
    if (!N || !effectivePerDay || !weekdays.length || !windowRange.valid) return null;
    const sendDays = Math.ceil(N / effectivePerDay);
    // días calendario aproximados: sendDays escalado por días activos/semana
    const calendarDays = Math.ceil(sendDays * (7 / weekdays.length));
    const perDayReal = Math.min(effectivePerDay, N);
    const interval = Math.max(1, Math.round(windowRange.minutes / perDayReal));
    return { sendDays, calendarDays, interval, perDayReal };
  }, [N, effectivePerDay, weekdays, windowRange]);

  const toggleWeekday = (d: number) => {
    setWeekdays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d].sort());
  };

  const canSubmit = !submitting && !loadingTotal && N > 0 && disclaimer
    && windowRange.valid && weekdays.length > 0 && effectivePerDay >= 1;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      // 1) Guarda la cadencia/ventana/días en la campaña; 2) programa (base congela la cola).
      await campaignsApi.setScheduleConfig(campaign.id, {
        msgs_per_day: effectivePerDay,
        window_start: windowRange.start,
        window_end: windowRange.end,
        weekdays,
      });
      const sch = await campaignsApi.schedule(campaign.id);
      const today = new Date();
      const end = new Date();
      end.setDate(end.getDate() + Math.max(0, (estimate?.calendarDays || 1) - 1));
      const res: ScheduleResult = {
        totalQueued: sch.queued,
        sendPlan: { msgsPerDay: effectivePerDay },
        avgIntervalMinutes: estimate?.interval || 0,
        startDay: today.toLocaleDateString('en-CA'),
        endDay: end.toLocaleDateString('en-CA'),
        msgsPerDayAdjusted: null,
        skipped: { noPhone: 0, duplicates: 0 },
      };
      setResult(res);
      onScheduled(res);
    } catch (e: any) {
      setError(e?.message || 'Error al programar el envío.');
    } finally {
      setSubmitting(false);
    }
  };

  const modal = (
    <div className="modal-overlay fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        className="modal-panel bg-surface rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-5 py-4 border-b border-line flex items-center justify-between sticky top-0 bg-surface z-10">
          <h3 className="text-lg font-medium text-content flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-blue-500" />
            {esAsistida ? 'Preparar la lista de envío' : 'Programar envío inteligente'}
          </h3>
          <button onClick={onClose} className="text-content-3 hover:text-content"><X className="h-5 w-5" /></button>
        </div>

        {result ? (
          /* ── Confirmación ── */
          <div className="p-5 space-y-4">
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 text-sm text-emerald-800">
              <p className="font-semibold mb-1">✅ Campaña programada</p>
              <p>{result.totalQueued} mensajes en cola · {result.sendPlan.msgsPerDay}/día
                (≈ 1 cada {result.avgIntervalMinutes} min) · del {result.startDay} al {result.endDay}.</p>
              {result.msgsPerDayAdjusted !== null && (
                <p className="mt-2 text-amber-700">ℹ️ Ajustamos a {result.msgsPerDayAdjusted}/día para respetar el
                  espaciado mínimo en tu ventana horaria.</p>
              )}
              {(result.skipped.noPhone > 0 || result.skipped.duplicates > 0) && (
                <p className="mt-2 text-emerald-700">Omitidos: {result.skipped.noPhone} sin teléfono,
                  {' '}{result.skipped.duplicates} duplicados.</p>
              )}
            </div>
            <button onClick={onClose}
              className="w-full px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700">
              Entendido
            </button>
          </div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Total */}
            <p className="text-sm text-content-2">
              {loadingTotal ? 'Contando clientes pendientes…'
                : pendingTotal === null ? 'No se pudo obtener el total de pendientes.'
                : <>Se enviará a <span className="font-semibold text-content">{pendingTotal}</span> clientes pendientes.</>}
            </p>

            {/* Aviso de FATIGA: dos campañas con el mismo criterio nunca son disjuntas, y lo
                único que las separa es el tiempo. Aquí se ve ANTES de programar. */}
            {fatiga && fatiga.afectados > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid="fatiga-aviso">
                <p className="font-medium flex items-center gap-1.5">
                  <AlertTriangle className="h-4 w-4 shrink-0" />
                  {fatiga.afectados} de estas {fatiga.total} personas ya recibieron otra campaña en los últimos {fatiga.days} días.
                </p>
                <p className="text-xs mt-1">
                  {fatiga.por_campana.map(p => `«${p.name}» (${p.n}${p.channel === 'email' ? ', por correo' : ''})`).join(' · ')}
                  {fatiga.ejemplos.length > 0 && (
                    <> — p. ej. {fatiga.ejemplos.slice(0, 3).map(e => `${e.nombre} hace ${e.hace_dias} d`).join(', ')}</>
                  )}
                </p>
                <p className="text-xs mt-1">Puedes continuar igual; solo te lo avisamos para que no les escribas dos veces seguidas.</p>
              </div>
            )}

            {/* MANUAL: el horario y el ritmo no gobiernan nada (envía una persona, de a
                uno, desde la cola). Prometerlos aquí sería mentir. Solo se prepara la lista. */}
            {esAsistida ? (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
                <p className="font-medium">Los mandas tú, uno por uno.</p>
                <p className="text-xs mt-1">
                  Al continuar solo se prepara la lista: no sale ningún mensaje hasta que pulses
                  «Enviar» en cada cliente, desde la cola de envío.
                </p>
                {pace && (
                  <p className="text-xs mt-1">
                    Aun así se respeta el tope de tu número ({pace.day_cap} al día · máximo{' '}
                    {pace.hour_cap} por hora): es lo que evita que te lo suspendan.
                  </p>
                )}
              </div>
            ) : (
            /* Ritmo — lo fija el motor por la antigüedad del número, no se elige */
            <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-900">
              <p className="flex items-center gap-2 font-medium">
                <Gauge className="h-4 w-4" />
                Ritmo de envío: {textoRitmo(pace) || 'calculando…'}
              </p>
              {pace && (
                <>
                  <p className="text-xs mt-1 text-sky-800">
                    Lo determina la antigüedad de tu WhatsApp ({pace.reason}). No se puede acelerar:
                    es lo que protege tu número de que WhatsApp lo suspenda.
                  </p>
                  {pace.day_cap < 20 && (
                    <p className="text-xs mt-1 text-sky-800">
                      Irá subiendo solo conforme tu número gane antigüedad{pace.repaired ? ' otra vez' : ''}.
                    </p>
                  )}
                  {typeof pace.usado_hoy === 'number' && (
                    <p className="text-xs mt-1 text-sky-800">
                      {textoConsumo(pace)} El cupo es del número, no de cada campaña.
                    </p>
                  )}
                  {!!pace.reserva_cumples && (
                    <p className="text-xs mt-1 text-sky-800">
                      Se guardan {pace.reserva_cumples} para las felicitaciones de cumpleaños de hoy:
                      esas no pueden esperar a mañana y una campaña sí.
                    </p>
                  )}
                  {N > pace.day_cap && (
                    <p className="text-xs mt-1 text-sky-800">
                      Con {N} {N === 1 ? 'cliente' : 'clientes'} en cola, la campaña tardará varios días.
                      Si necesitas llegar antes, reduce la audiencia — no el ritmo.
                    </p>
                  )}
                  {effectivePerDay < pace.day_cap && (
                    <p className="text-xs mt-1 font-medium text-sky-900">
                      Le pediste {effectivePerDay} al día a esta campaña, así que se usarán {effectivePerDay}
                      {' '}de los {pace.day_cap} que permite tu número.
                    </p>
                  )}
                </>
              )}
            </div>
            )}

            {/* Tope propio: el motor pone el techo, el negocio puede quedarse por debajo.
                No es una velocidad "extra" — es el mismo msgs_per_day de siempre, que el
                worker ya respetaba y que la pantalla había dejado de dejar escribir. */}
            {!esAsistida && pace && (
              <div>
                <p className="text-sm font-medium text-content-2 mb-2">¿Cuántos al día, como máximo?</p>
                <div className="flex flex-wrap items-center gap-2">
                  <input
                    type="number" min={1} max={techoNumero}
                    value={topePropio ?? techoNumero}
                    onChange={e => {
                      const v = parseInt(e.target.value, 10);
                      setTopePropio(Number.isFinite(v) ? Math.max(1, Math.min(v, techoNumero)) : null);
                    }}
                    className="w-20 rounded-md border-line text-sm"
                  />
                  <span className="text-sm text-content-2">al día</span>
                  {effectivePerDay < techoNumero && (
                    <button type="button" onClick={() => setTopePropio(null)}
                      className="text-xs text-blue-600 hover:underline">
                      usar el máximo ({techoNumero})
                    </button>
                  )}
                </div>
                <p className="text-xs text-content-3 mt-1">
                  Tu número permite hasta {techoNumero} al día. Aquí puedes pedir <strong>menos</strong>
                  {' '}—nunca más—: sirve para no quemar la lista de golpe. Si lo dejas en {techoNumero},
                  se usa todo el cupo disponible.
                </p>
              </div>
            )}

            {/* Horario y días: en MANUAL no aplican (envía una persona cuando quiere). */}
            {!esAsistida && (
            <div>
              <p className="text-sm font-medium text-content-2 mb-2">Horario de envío</p>
              <div className="flex flex-wrap gap-2">
                {WINDOW_PRESETS.map(w => (
                  <button key={w.key} onClick={() => setWindowKey(w.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                      windowKey === w.key ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-line text-content-2 hover:bg-surface-2'
                    }`}>
                    {w.label}{w.start && ` ${w.start}–${w.end}`}
                  </button>
                ))}
              </div>
              {windowKey === 'custom' && (
                <div className="flex items-center gap-2 mt-2 text-sm">
                  <span className="text-content-2">De</span>
                  <input type="time" value={customStart} onChange={e => setCustomStart(e.target.value)}
                    className="rounded-md border-line text-sm" />
                  <span className="text-content-2">a</span>
                  <input type="time" value={customEnd} onChange={e => setCustomEnd(e.target.value)}
                    className="rounded-md border-line text-sm" />
                </div>
              )}
              {!windowRange.valid && (
                <p className="text-xs text-red-600 mt-1">La ventana debe ser de al menos 2 horas.</p>
              )}
              <div className="flex items-center gap-1.5 mt-3">
                <span className="text-sm text-content-2 mr-1">Días:</span>
                {[1, 2, 3, 4, 5, 6, 0].map(d => (
                  <button key={d} onClick={() => toggleWeekday(d)}
                    className={`w-8 h-8 rounded-full text-xs font-semibold border transition-colors ${
                      weekdays.includes(d) ? 'bg-blue-600 border-blue-600 text-white' : 'border-line text-content-3 hover:bg-surface-2'
                    }`}>
                    {WEEKDAY_LABELS[d]}
                  </button>
                ))}
              </div>
            </div>
            )}

            {/* Resumen estimado — sin sentido en manual: no hay cadencia que estimar. */}
            {!esAsistida && estimate && N > 0 && (
              <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm text-sky-800">
                <p><span className="font-semibold">{Math.min(effectivePerDay, N)}/día</span> · ≈ 1 mensaje cada{' '}
                  <span className="font-semibold">{estimate.interval} min</span> · terminará en{' '}
                  <span className="font-semibold">{estimate.sendDays} {estimate.sendDays === 1 ? 'día' : 'días'} de envío</span>
                  {weekdays.length < 7 && estimate.calendarDays > estimate.sendDays && ` (~${estimate.calendarDays} días calendario)`}.
                </p>
                <p className="text-xs mt-1 text-sky-700">Los mensajes salen con pausas aleatorias dentro del horario,
                  como si los enviaras tú.</p>
              </div>
            )}

            {/* Disclaimer */}
            <label className="flex items-start gap-2 text-xs rounded-lg border border-line bg-surface-2 text-content-2 p-3 cursor-pointer">
              <input type="checkbox" checked={disclaimer} onChange={e => setDisclaimer(e.target.checked)}
                className="mt-0.5 rounded border-line" />
              <span>
                Entiendo que el envío masivo por WhatsApp puede ocasionar que WhatsApp restrinja o suspenda mi número,
                y acepto ese riesgo. Si WhatsApp lo suspende, Lealify no puede recuperarlo.
              </span>
            </label>

            {error && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
            )}

            <div className="flex gap-2">
              <button onClick={onClose}
                className="flex-1 px-4 py-2 rounded-md border border-line text-sm font-medium text-content-2 hover:bg-surface-2">
                Cancelar
              </button>
              <button onClick={handleSubmit} disabled={!canSubmit}
                className="flex-1 px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2">
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                {esAsistida ? 'Preparar la lista' : 'Programar envío'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Portal a body: el modal usa position:fixed y la vista de preview tiene un
  // ancestro con transform (animate-fade-in-up), que anclaría el fixed a ese
  // contenedor y lo descolocaría. createPortal lo saca de ese árbol.
  return createPortal(modal, document.body);
};

export default SendPlanModal;
